import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, LayoutGrid, List as ListIcon, Search, ChevronDown, Trash2, AlertTriangle, Star, ChevronUp, MoveVertical, FileText } from "lucide-react";
import api from "../lib/api";
import PhaseChip from "../components/PhaseChip";
import Modal from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import { usePersistedState } from "../lib/persisted";
import { JOBCARD_PHASES } from "../lib/enums";
import UnseenDot from "../components/UnseenDot";
import EntityAlertBadge from "../components/EntityAlertBadge";
import useUnseen from "../lib/useUnseen";
import useEntityAlerts from "../lib/useEntityAlerts";
import useFocusOnMount from "../lib/useFocusOnMount";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { JCProgressBar } from "../components/JCProgress";
import { useListOrdering } from "../lib/useListOrdering";

// Subset shown as kanban columns (typical operational workflow).
// All 13+ phases remain selectable from the per-card phase select.
// Wave 25.13 — In-progress promoted to first so the active work is always
// the first thing the user sees on /jobcards (decision: default in-progress
// at top).
const KANBAN_PHASES = [
  { key: "in_progress", label: "In progress" },
  { key: "scheduled", label: "Scheduled" },
  { key: "ready", label: "Ready" },
  { key: "picking", label: "Picking" },
  { key: "created", label: "Created" },
  { key: "paused", label: "Paused" },
  { key: "on_site_complete", label: "On-site completed" },
  { key: "signed_off", label: "Signed off" },
  { key: "ready_to_invoice", label: "To invoice" },
  { key: "invoice_sent", label: "Invoice sent" },
  { key: "closed", label: "Closed" },
  { key: "cancelled", label: "Cancelled" },
];
// [Iter 336 — 2026-02-25] Legacy phases (`complete`, `paid`, `quote_client`)
// are now migrated on the backend to their approved equivalents (see
// `_LEGACY_PHASE_MIGRATION` in `backend/routes/jobcards.py`). The
// "Other / legacy" catch-all bucket has been removed — every JC now
// lands in one of the 12 approved columns above. If a stray phase
// ever slips through, it maps to `created` in the API layer so
// nothing is silently dropped from the board.
// Phases considered "closed" — hidden from default list view; user can flip to show them.
// Iter 415.1 (Dirk, 2026-02-28) — `invoice_sent` is NOT a closed
// phase. It's the "invoice sent, waiting for customer payment" state.
// Only once payment is received does the JC become closed / paid.
// The old grouping hid all 11 invoice-sent jobs from the main list
// unless the user toggled Show closed, which broke the collection loop.
const CLOSED_PHASES = new Set(["closed", "cancelled", "paid"]);

const PHASES = KANBAN_PHASES;

// Sort order for list view: follows JOBCARD_PHASES enum order
const PHASE_ORDER = JOBCARD_PHASES.reduce(
  (acc, p, i) => ({ ...acc, [p.key]: i }),
  {},
);

export default function JobCards() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = user && (user.role === "owner" || user.role === "admin" || user.role === "manager");
  const [view, setView] = usePersistedState("jobcards.view", "kanban");
  // [2026-02-19] Mobile fix: the kanban view is a horizontal-scroll
  // container with min-width 1100px. On Android, that container's
  // touch-pan-x gesture wins the arbiter and blocks vertical page
  // scroll until the user lifts and re-touches off-axis. Force list
  // view on phones (kanban is unusable on a 360px viewport anyway)
  // while preserving the user's saved preference for desktop.
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(max-width: 767px)").matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    try { mq.addEventListener("change", onChange); }
    catch { mq.addListener(onChange); }  // Safari < 14
    return () => {
      try { mq.removeEventListener("change", onChange); }
      catch { mq.removeListener(onChange); }
    };
  }, []);
  const effectiveView = isMobile ? "list" : view;
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [q, setQ] = useState("");
  const [showClosed, setShowClosed] = usePersistedState("jobcards.showClosed", false);
  const [collapsed, setCollapsed] = usePersistedState("jobcards.collapsed", {});
  const [confirmDelete, setConfirmDelete] = useState(null); // {id,title}
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  // Wave 25.13 — per-user pin + manual reorder (decision 1c).
  const ordering = useListOrdering("jobcards", user?.id);
  const isMine = (j) => j.technician_id === user?.id || (Array.isArray(j.staff_ids) && j.staff_ids.includes(user?.id));
  // Wave 26.9 — bubble in-progress JCs to the top of each phase column.
  // (Within a phase column, hot is just `phase === in_progress`; in any
  // mixed list, this still works because pending/scheduled return false.)
  const isHot = (j) => j.phase === "in_progress";

  const load = async (opts) => {
    // 2026-02-20 (iter 173, Dirk) — when useAutoRefresh calls this on
    // its 30s tick it passes `{silent:true}` so we skip the loading
    // skeleton and let the existing list stay visible. React reconciles
    // the array diff in place, so the scroll position is preserved.
    // Only the FIRST load (from useEffect) shows the skeleton.
    const silent = !!opts?.silent;
    if (!silent) {
      setLoadError("");
      setLoading(true);
    }
    // Render job cards as fast as possible — don't block on /customers or
    // /sites (they can each be 700KB+ and may legitimately time out on
    // poor mobile signal). Names + site labels resolve in the background.
    try {
      const j = await api.get("/jobcards");
      setJobs(Array.isArray(j.data) ? j.data : []);
      if (silent) setLoadError("");
    } catch (e) {
      if (!silent) {
        setLoadError(e?.response?.data?.detail || e?.message || "Could not load job cards.");
      }
      // Silent failures: swallow — keep current data on screen rather
      // than blanking with an error toast the user didn't ask for.
    } finally {
      if (!silent) setLoading(false);
    }
    // Tolerant background fetches — fall through to empty arrays so the
    // table still renders with raw IDs if the lookup tables are slow.
    api.get("/customers").then((r) => setCustomers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/sites").then((r) => setSites(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);
  // Auto-refresh while the tab is foreground (Wave 19 — concurrent-edit safety).
  // 30 s timer + immediate refetch when the user returns to the tab. No calls
  // fire while hidden, so cost is bounded.
  useAutoRefresh(load, { intervalMs: 30_000 });

  // Awareness Contract — DESIGN_CONTRACT §7.
  const { isUnseen, touch, dismiss } = useUnseen("jobcards", jobs);
  useEffect(() => { touch(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useFocusOnMount({ ready: jobs.length > 0, deps: [jobs.length] });

  // Wave 19 #84 — directed-alert pulses per row (rose badge with count).
  // The list also re-loads alert counts whenever the user returns from a
  // detail page, so opening a JC clears its badge in-place.
  const { unreadFor: jcUnreadFor } = useEntityAlerts("jobcard");

  const createDraftAndOpen = () => {
    // Full-page editor — customer + site are mandatory. The previous
    // "draft + edit" path bypassed them and inserted orphan job cards
    // (DESIGN_CONTRACT §1: every entity links back to a customer).
    navigate("/jobcards/new");
  };

  const remove = async () => {
    if (!confirmDelete) return;
    await api.delete(`/jobcards/${confirmDelete.id}`);
    setConfirmDelete(null);
    load();
  };

  const customerName = (id) => {
    if (!id) return "—";
    const c = customers.find((x) => x.id === id);
    if (c) return c.name || `Customer ${id.slice(0, 6)}`;
    // Customers fetch may still be in flight or have failed. Show a stable
    // truncated id so the row is still visually distinct & clickable.
    return `Customer ${id.slice(0, 6)}…`;
  };
  const customerRef = (id) => customers.find((c) => c.id === id)?.external_ref;
  const siteName = (id) => {
    if (!id) return undefined;
    const s = sites.find((x) => x.id === id);
    return s ? s.name : undefined;
  };

  // Search across title, description, customer name + ref, site name, phase, external_ref
  const visibleJobs = useMemo(() => {
    const term = (q || "").trim().toLowerCase();
    return jobs.filter((j) => {
      if (!showClosed && CLOSED_PHASES.has(j.phase)) return false;
      if (!term) return true;
      const hay = [
        j.title, j.description, j.phase, j.external_ref,
        customerName(j.customer_id), customerRef(j.customer_id), siteName(j.site_id),
      ].map((x) => (x ? String(x).toLowerCase() : "")).join(" | ");
      return hay.includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, q, showClosed, customers, sites]);

  const toggleCollapse = (key) => setCollapsed({ ...collapsed, [key]: !collapsed[key] });

  return (
    <div className="space-y-5" data-testid="jobcards-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Field operations</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            Job cards
            <span className="text-base font-normal text-[#647087]">· {visibleJobs.length}</span>
          </h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, customer, site, phase…"
              className="hv-input pl-8 py-2 text-xs w-72"
              data-testid="jobcards-search"
            />
          </div>
          <label className="text-xs text-[#a0aabf] inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={showClosed}
              onChange={(e) => setShowClosed(e.target.checked)}
              data-testid="jobcards-show-closed"
            />
            Show closed
          </label>
          <div className="hidden md:flex border border-white/10 rounded-md overflow-hidden">
            <button
              onClick={() => setView("kanban")}
              data-testid="view-kanban"
              className={`px-3 py-2 text-xs ${view === "kanban" ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#647087]"}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="view-list"
              className={`px-3 py-2 text-xs ${view === "list" ? "bg-[#1fb6c1]/15 text-[#1fb6c1]" : "text-[#647087]"}`}
            >
              <ListIcon className="w-4 h-4" />
            </button>
          </div>
          <Link
            to="/jobcards/templates"
            className="hv-btn hv-btn-ghost"
            data-testid="jobcards-templates-btn"
            title="Manage reusable task scaffolds you can apply when creating new job cards."
          >
            <FileText className="w-4 h-4" /> Templates
          </Link>
          <button onClick={createDraftAndOpen} className="hv-btn hv-btn-primary" data-testid="job-new-btn" disabled={!isManager} style={!isManager ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
            <Plus className="w-4 h-4" /> New job card
          </button>
        </div>
      </div>

      {loading && (
        <div className="hv-card p-6 text-center" data-testid="jobcards-loading">
          <div className="text-sm text-[#a0aabf]">Loading job cards…</div>
        </div>
      )}
      {!loading && loadError && (
        <div className="hv-card p-4 border border-rose-500/30 bg-rose-500/5" data-testid="jobcards-error">
          <div className="text-sm text-rose-300 font-medium">Could not load job cards.</div>
          <div className="text-xs text-[#a0aabf] mt-1">{loadError}</div>
          <button
            type="button"
            onClick={load}
            className="hv-btn hv-btn-ghost text-xs mt-2"
            data-testid="jobcards-retry"
          >
            Try again
          </button>
        </div>
      )}
      {!loading && !loadError && jobs.length === 0 && (
        <div className="hv-card p-8 text-center" data-testid="jobcards-empty">
          <div className="text-sm text-[#a0aabf]">No job cards yet.</div>
        </div>
      )}

      {!loading && !loadError && jobs.length > 0 && (effectiveView === "kanban" ? (
        <div className="overflow-x-auto pb-3">
          <div className="flex gap-3 min-w-[1100px]">
            {(() => {
              // [Iter 336] Legacy bucket removed — every JC lands in an
              // approved phase after the backend migration.
              return PHASES.map((p) => {
              const rawItems = visibleJobs.filter((j) => j.phase === p.key);
              const items = ordering.sortItems({
                items: rawItems,
                groupKey: `kanban_${p.key}`,
                isMine,
                isHot,
              });
              const isCollapsed = collapsed[`kanban_${p.key}`];
              return (
                <div key={p.key} className="w-72 flex-shrink-0">
                  <button
                    onClick={() => toggleCollapse(`kanban_${p.key}`)}
                    className="w-full flex items-center justify-between px-2 py-2 text-left hover:bg-white/5 rounded transition"
                    data-testid={`kanban-fold-${p.key}`}
                  >
                    <div className="text-xs uppercase tracking-[0.2em] text-[#a0aabf] font-bold flex items-center gap-2">
                      <ChevronDown className={`w-3 h-3 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                      {p.label}
                    </div>
                    <span className="text-xs text-[#647087]">{items.length}</span>
                  </button>
                  {!isCollapsed && (
                    <div className="bg-[#0a0b0e] border border-white/5 rounded-md p-2 min-h-[200px] space-y-2">
                      {items.map((j, idx) => (
                        <div
                          key={j.id}
                          className={`hv-card p-3 block group relative ${isUnseen(j) ? "hv-unseen" : ""} ${ordering.isPinned(j.id) ? "ring-1 ring-amber-400/40" : ""}`}
                          data-testid={`job-card-${j.id}`}
                          data-focus-id={j.id}
                        >
                          <Link
                            to={`/jobcards/${j.id}`}
                            onClick={() => dismiss(j.id)}
                            className="block"
                          >
                            <div className="flex items-start gap-2">
                              <UnseenDot unseen={isUnseen(j)} testId={`jc-kanban-unseen-${j.id}`} />
                              <div className="min-w-0 flex-1">
                            {j.short_id && (
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-[10px] font-mono text-[#1fb6c1]">#{j.short_id}</div>
                                <EntityAlertBadge count={jcUnreadFor(j.id)} testId={`jc-kanban-alerts-${j.id}`} />
                              </div>
                            )}
                            <div className="font-medium leading-tight pr-6">{j.title}</div>
                            <div className="text-xs text-[#647087] mt-1">{customerName(j.customer_id)}</div>
                            {j.scheduled_at && (
                              <div className="text-xs text-[#1fb6c1] mt-1">
                                {new Date(j.scheduled_at).toLocaleString('en-GB')}
                              </div>
                            )}
                            {(j.checklist || []).length > 0 && (
                              <div className="mt-2">
                                <JCProgressBar tasks={j.checklist} compact />
                              </div>
                            )}
                              </div>
                            </div>
                          </Link>
                          {/* Pin star + reorder controls (Wave 25.13). */}
                          <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 opacity-60 hover:opacity-100 group-hover:opacity-100 transition">
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); ordering.togglePin(j.id); }}
                              className={`p-1 rounded ${ordering.isPinned(j.id) ? "text-amber-300" : "text-[#647087] hover:text-amber-300"}`}
                              title={ordering.isPinned(j.id) ? "Unpin" : "Pin to top"}
                              aria-label={ordering.isPinned(j.id) ? "Unpin job card" : "Pin job card to top"}
                              data-testid={`jc-pin-${j.id}`}
                            >
                              <Star className={`w-3.5 h-3.5 ${ordering.isPinned(j.id) ? "fill-current" : ""}`} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); ordering.moveUp(`kanban_${p.key}`, j.id, items.map((it) => it.id)); }}
                              disabled={idx === 0}
                              className="p-1 rounded text-[#647087] hover:text-[#1fb6c1] disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move up"
                              aria-label="Move up"
                              data-testid={`jc-up-${j.id}`}
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); ordering.moveDown(`kanban_${p.key}`, j.id, items.map((it) => it.id)); }}
                              disabled={idx === items.length - 1}
                              className="p-1 rounded text-[#647087] hover:text-[#1fb6c1] disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move down"
                              aria-label="Move down"
                              data-testid={`jc-down-${j.id}`}
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            });
            })()}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {(() => {
            // [Iter 336] Legacy bucket removed — see kanban view above.
            return PHASES.map((p) => {
            const rawItems = visibleJobs.filter((j) => j.phase === p.key);
            const items = ordering.sortItems({
              items: rawItems,
              groupKey: `list_${p.key}`,
              isMine,
              isHot,
            });
            if (!items.length) return null;
            const isCollapsed = collapsed[`list_${p.key}`];
            return (
              <div key={p.key} className="hv-card overflow-hidden" data-testid={`jc-group-${p.key}`}>
                <button
                  onClick={() => toggleCollapse(`list_${p.key}`)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition"
                  data-testid={`jc-fold-${p.key}`}
                >
                  <div className="flex items-center gap-2">
                    <ChevronDown className={`w-4 h-4 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                    <PhaseChip value={p.key} />
                    <span className="text-sm text-[#a0aabf]">{items.length} job{items.length === 1 ? "" : "s"}</span>
                  </div>
                </button>
                {!isCollapsed && (
                  <>
                    {/* Mobile (single-column card list). Tables collapse miserably
                         when 5 columns get squeezed into a phone width — every
                         word wraps onto its own line. We render compact cards
                         instead, surfaced via the same row click → detail. */}
                    <div className="md:hidden border-t border-white/10 divide-y divide-white/5" data-testid={`jc-mobile-list-${p.key}`}>
                      {items.map((j, idx) => (
                        <div
                          key={j.id}
                          onClick={(e) => {
                            if (e.target.closest("a, button")) return;
                            dismiss(j.id);
                            navigate(`/jobcards/${j.id}`);
                          }}
                          className={`px-3 py-3 active:bg-white/5 transition ${isUnseen(j) ? "hv-unseen" : ""} ${ordering.isPinned(j.id) ? "bg-amber-500/[0.04]" : ""}`}
                          data-testid={`jc-mobile-row-${j.id}`}
                          data-focus-id={j.id}
                        >
                          <div className="flex items-start gap-2">
                            <UnseenDot unseen={isUnseen(j)} testId={`jc-unseen-m-${j.id}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                {j.short_id && (
                                  <span className="text-[10px] font-mono text-[#1fb6c1]">#{j.short_id}</span>
                                )}
                                <EntityAlertBadge count={jcUnreadFor(j.id)} testId={`jc-mobile-alerts-${j.id}`} />
                              </div>
                              <Link
                                to={`/jobcards/${j.id}`}
                                className="text-sm font-medium leading-snug block mt-0.5 break-words hover:text-amber-300 transition"
                                data-testid={`jc-mobile-title-${j.id}`}
                              >
                                {j.title}
                              </Link>
                              <div className="text-[11px] text-[#a0aabf] mt-1.5 leading-relaxed space-y-0.5">
                                <div className="truncate" data-testid={`jc-mobile-customer-${j.id}`}>
                                  {/* Iter 427.p20 — customer link removed; whole card opens the jobcard. */}
                                  {customerName(j.customer_id)}
                                  {siteName(j.site_id) ? (
                                    <span className="text-[#647087]"> · {siteName(j.site_id)}</span>
                                  ) : null}
                                </div>
                                {j.scheduled_at && (
                                  <div className="text-[#647087]">
                                    {new Date(j.scheduled_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                )}
                                {j.external_ref && (
                                  <div className="font-mono text-[#647087]">{j.external_ref}</div>
                                )}
                              </div>
                              {(j.checklist || []).length > 0 && (
                                <div className="mt-2">
                                  <JCProgressBar tasks={j.checklist} compact />
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-center gap-0.5 shrink-0">
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); ordering.togglePin(j.id); }}
                                className={`p-1.5 rounded ${ordering.isPinned(j.id) ? "text-amber-300" : "text-[#647087] hover:text-amber-300"}`}
                                title={ordering.isPinned(j.id) ? "Unpin" : "Pin to top"}
                                aria-label={ordering.isPinned(j.id) ? "Unpin job card" : "Pin job card to top"}
                                data-testid={`jc-mobile-pin-${j.id}`}
                              >
                                <Star className={`w-4 h-4 ${ordering.isPinned(j.id) ? "fill-current" : ""}`} />
                              </button>
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); ordering.moveUp(`list_${p.key}`, j.id, items.map((it) => it.id)); }}
                                disabled={idx === 0}
                                className="p-1 rounded text-[#647087] disabled:opacity-30"
                                title="Move up"
                                aria-label="Move up"
                                data-testid={`jc-mobile-up-${j.id}`}
                              >
                                <ChevronUp className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); ordering.moveDown(`list_${p.key}`, j.id, items.map((it) => it.id)); }}
                                disabled={idx === items.length - 1}
                                className="p-1 rounded text-[#647087] disabled:opacity-30"
                                title="Move down"
                                aria-label="Move down"
                                data-testid={`jc-mobile-down-${j.id}`}
                              >
                                <ChevronDown className="w-3.5 h-3.5" />
                              </button>
                              {isManager && (
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setConfirmDelete({ id: j.id, title: j.title });
                                  }}
                                  className="p-1.5 rounded text-rose-300 hover:text-rose-200"
                                  data-testid={`jc-mobile-delete-${j.id}`}
                                  title="Delete job card"
                                  aria-label="Delete job card"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop table — Wave 25.13: pin star + reorder + progress column */}
                    <table className="hidden md:table w-full text-sm border-t border-white/10">
                    <tbody>
                      {items.map((j, idx) => (
                        <tr
                          key={j.id}
                          className={`border-b border-white/5 hover:bg-amber-500/5 transition cursor-pointer ${isUnseen(j) ? "hv-unseen" : ""} ${ordering.isPinned(j.id) ? "bg-amber-500/[0.04]" : ""}`}
                          onClick={(e) => {
                            // Don't trigger when the user clicks an inline link or the delete button.
                            if (e.target.closest("a, button")) return;
                            dismiss(j.id);
                            navigate(`/jobcards/${j.id}`);
                          }}
                          data-testid={`jc-row-tr-${j.id}`}
                          data-focus-id={j.id}
                        >
                          <td className="px-2 py-3 w-8">
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); ordering.togglePin(j.id); }}
                              className={`p-1 rounded transition ${ordering.isPinned(j.id) ? "text-amber-300" : "text-[#647087]/50 hover:text-amber-300"}`}
                              title={ordering.isPinned(j.id) ? "Unpin" : "Pin to top"}
                              aria-label={ordering.isPinned(j.id) ? "Unpin" : "Pin"}
                              data-testid={`jc-list-pin-${j.id}`}
                            >
                              <Star className={`w-3.5 h-3.5 ${ordering.isPinned(j.id) ? "fill-current" : ""}`} />
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <Link to={`/jobcards/${j.id}`} className="hover:text-amber-300 transition flex items-start gap-2" data-testid={`jc-row-${j.id}`}>
                              <UnseenDot unseen={isUnseen(j)} testId={`jc-unseen-${j.id}`} />
                              <div className="min-w-0">
                              {j.short_id && (
                                <div className="flex items-center gap-2">
                                  <div className="text-[10px] font-mono text-[#1fb6c1]">#{j.short_id}</div>
                                  <EntityAlertBadge count={jcUnreadFor(j.id)} testId={`jc-list-alerts-${j.id}`} />
                                </div>
                              )}
                              <div className="font-medium">{j.title}</div>
                              {j.external_ref && (
                                <div className="text-[10px] font-mono text-[#647087] mt-0.5">{j.external_ref}</div>
                              )}
                              </div>
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-[#a0aabf]">
                            {/* Iter 427.p20 — Dirk 2026-09-06: kill the
                                customer link on the row; the whole row
                                is the jobcard click target. Users with
                                small screens / dirty hands kept opening
                                the customer profile by miss-tapping.
                                Open Customer stays available inside the
                                job card. */}
                            <span data-testid={`jc-list-customer-${j.id}`}>
                              {customerName(j.customer_id)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-[#a0aabf]">{siteName(j.site_id) || "—"}</td>
                          <td className="px-4 py-3 text-[#a0aabf]">
                            {j.scheduled_at ? new Date(j.scheduled_at).toLocaleString('en-GB') : "—"}
                          </td>
                          <td className="px-4 py-3 w-44">
                            {(j.checklist || []).length > 0 ? (
                              <JCProgressBar tasks={j.checklist} compact />
                            ) : (
                              <span className="text-[10px] text-[#647087]">—</span>
                            )}
                          </td>
                          <td className="px-2 py-3 text-right whitespace-nowrap">
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); ordering.moveUp(`list_${p.key}`, j.id, items.map((it) => it.id)); }}
                              disabled={idx === 0}
                              className="p-1 rounded text-[#647087] hover:text-[#1fb6c1] disabled:opacity-30"
                              title="Move up"
                              data-testid={`jc-list-up-${j.id}`}
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); ordering.moveDown(`list_${p.key}`, j.id, items.map((it) => it.id)); }}
                              disabled={idx === items.length - 1}
                              className="p-1 rounded text-[#647087] hover:text-[#1fb6c1] disabled:opacity-30"
                              title="Move down"
                              data-testid={`jc-list-down-${j.id}`}
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                            {isManager && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setConfirmDelete({ id: j.id, title: j.title });
                                }}
                                className="p-1 rounded text-rose-300 hover:text-rose-200 ml-1"
                                data-testid={`jc-delete-${j.id}`}
                                title="Delete job card"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    </table>
                  </>
                )}
              </div>
            );
          });
          })()}
          {visibleJobs.length === 0 && <div className="hv-card p-6 text-center text-[#647087]">No job cards.</div>}
        </div>
      ))}

      {confirmDelete && (
        <Modal open onClose={() => setConfirmDelete(null)} title="Delete job card" testId="jc-delete-modal" maxWidth="max-w-md">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-rose-500/15 border border-rose-500/40 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-300" />
              </div>
              <div>
                <p className="text-sm">
                  Permanently delete job card <span className="font-bold text-white">"{confirmDelete.title}"</span>?
                </p>
                <p className="text-xs text-[#a0aabf] mt-1">
                  This cannot be undone. The job card and its checklist will be removed for all users.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
              <button onClick={() => setConfirmDelete(null)} className="hv-btn hv-btn-ghost" data-testid="jc-delete-cancel">
                Cancel
              </button>
              <button onClick={remove} className="hv-btn hv-btn-primary bg-rose-500 hover:bg-rose-400" data-testid="jc-delete-confirm">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
